import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Clock, ChevronRight } from 'lucide-react';

import PublicNavBar from '@/components/landing/PublicNavBar';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang, useHreflangAlternates } from '@/lib/useHreflang';   // 2026-02 · full 7-locale hreflang for blog posts
import StructuredData from '@/components/seo/StructuredData'; // 2026-02 · standardised JSON-LD injection

const API = process.env.REACT_APP_BACKEND_URL;
const NON_EN_LOCALES = ['fr', 'de', 'es', 'pt', 'zh'];

/* 2026-10 — Public blog post. Full Article + FAQPage (+ Person/
 * Organization) JSON-LD is injected from the backend response so
 * crawlers and LLM citation tools get E-E-A-T signal on first paint.
 * 2026-02 — Added i18n UI chrome and full hreflang for locale blog posts. */
export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [jsonLd, setJsonLd] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Per-post locale alternates from backend — when present, each locale links
  // to the correct sibling slug rather than assuming same slug across locales.
  const [hreflangAlts, setHreflangAlts] = useState(null);

  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const isLocale = NON_EN_LOCALES.includes(lang);
  const blogIndexPath  = isLocale ? `/${lang}/blog` : '/blog';
  const bookstorePath  = isLocale ? `/${lang}/bookstore` : '/bookstore';
  const relatedPostPath = (s) => isLocale ? `/${lang}/blog/${s}` : `/blog/${s}`;

  useSeoMeta(`/blog/${slug}`, post ? {
    title: `${post.title} | The Loom Blog`,
    description: post.meta_description || post.dek || '',
    keywords: post.meta_keywords || (post.tags || []).join(', '),
    og_image: post.cover_image_url || '',
  } : {});

  // Inject hreflang for /blog/{slug}, /fr/blog/{slug}, etc.
  // When the API returns per-post alternates (different slug per locale), use those.
  // Otherwise fall back to the symmetric path approach (same slug, all locales).
  useHreflangAlternates(hreflangAlts);                              // 2026-02 · precise per-locale slugs
  useHreflang(slug ? `blog/${slug}` : 'blog', !hreflangAlts);      // fallback for legacy posts

  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/blog/${slug}`).then((r) => {
      if (!active) return;
      setPost(r.data?.post || null);
      setRelated(r.data?.related || []);
      setJsonLd(r.data?.json_ld || null);
      setHreflangAlts(r.data?.hreflang_alternates || null);
    }).catch(() => { if (active) setNotFound(true); });
    return () => { active = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3" data-testid="blog-post-not-found">
        <p className="text-sm text-muted-foreground">{t('blog.notFound')}</p>
        <button type="button" onClick={() => navigate(blogIndexPath)} className="text-sm text-primary hover:underline">
          {t('blog.backToBlogBtn')}
        </button>
      </div>
    );
  }
  if (!post) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground" data-testid="blog-post-loading">{t('blog.loadingPost')}</div>;
  }

  return (
    <div className="h-full overflow-y-auto bg-background text-foreground" data-testid="blog-post-page">

      <StructuredData id="blog-post" data={jsonLd} />
      <PublicNavBar
        onSignIn={() => { window.location.href = '/login'; }}
        onSignUp={() => { window.location.href = '/signup'; }}
      />

      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {post.cover_image_url && (
          <img
            src={post.cover_image_url.startsWith('/') ? `${API}${post.cover_image_url}` : post.cover_image_url}
            alt=""
            className="mb-6 h-64 w-full rounded-lg object-cover"
          />
        )}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="blog-post-title">{post.title}</h1>
        {post.dek && <p className="mt-3 text-lg leading-relaxed text-muted-foreground">{post.dek}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
          <span>{t('blog.byAuthor', { name: post.author_name })}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.reading_minutes} {t('blog.minRead')}</span>
          {(post.tags || []).map((tag) => <span key={tag} className="rounded-full bg-muted px-2 py-0.5">{tag}</span>)}
        </div>

        <div
          className="mt-8 max-w-none text-[15px] leading-relaxed [&>h2]:mt-8 [&>h2]:text-2xl [&>h2]:font-semibold [&>h3]:mt-6 [&>h3]:text-lg [&>h3]:font-semibold [&>p]:mt-4 [&>ul]:mt-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ol]:mt-4 [&>ol]:list-decimal [&>ol]:pl-6 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground"
          data-testid="blog-post-body"
        >
          <ReactMarkdown>{post.body_markdown}</ReactMarkdown>
        </div>

        {(post.faq || []).length > 0 && (
          <section className="mt-10 border-t border-border pt-6" data-testid="blog-post-faq">
            <h2 className="text-xl font-semibold">{t('blog.faqSection')}</h2>
            <div className="mt-4 space-y-4">
              {post.faq.map((f, i) => (
                <div key={i}>
                  <h3 className="text-base font-semibold">{f.question}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-10 border-t border-border pt-6" data-testid="blog-post-related">
            <h2 className="text-xl font-semibold">{t('blog.relatedReading')}</h2>
            <div className="mt-4 space-y-2">
              {related.map((r) => (
                <Link key={r.slug} to={relatedPostPath(r.slug)} className="flex items-center justify-between rounded-md border border-border p-3 text-sm hover:border-primary" data-testid={`blog-related-link-${r.slug}`}>
                  <span>{r.title}</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 rounded-lg border border-border bg-card p-5" data-testid="blog-post-author-bio">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('blog.aboutAuthor')}</h2>
          <p className="mt-2 text-sm leading-relaxed">{post.author_bio}</p>
        </div>
      </article>
    </div>
  );
}
